// ============================================================================
// PairRow — Personal Finance V2 (Blueprint §8) — CORRIGIDO
// Renderiza UMA linha da tabela financeira com layout padrao de mercado:
//
// Colunas: DATA | TIPO | DESCRIÇÃO | CONTA | RESPONSÁVEL | CATEGORIA | STATUS | VALOR (R$) | AÇÕES
//
// Correcoes aplicadas:
// 1. CATEGORIA exibe t.group (grupo pai: ESTILO, MORADIA, PETS, etc)
// 2. AÇÕES em coluna separada — nunca dentro do VALOR
// ============================================================================

import React from "react";

const formatNumber = (val) =>
  new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(Number(val) || 0));
const stripArt = (s) =>
  String(s || "")
    .replace(/\s*\(Art\.\s*\d+[^)]*\)/gi, "")
    .replace(/\s+->\s+/g, " · ")
    .trim();

// Badge de TIPO: laranja para Parcelado, cinza para Única
function TipoBadge({ t }) {
  // Reforço (achado real 18/09/2026): o campo `t.parcela` podia se perder
  // ao editar/duplicar (corrigido na origem em PostingModal.jsx), fazendo
  // a etiqueta cair pra "Mensal" mesmo com a descrição mostrando "(N/T)".
  // A regex aceita tanto "parcela N/T" quanto só "(N/T)" no fim do texto,
  // como segunda camada de proteção, não a única.
  const parcelaMatch = (t.description || "").match(
    /(?:parcela\s+)?\((\d+\/\d+)\)\s*$/i,
  );
  const parcela = t.parcela || (parcelaMatch ? parcelaMatch[1] : null);
  // Achado real 18/09/2026: a etiqueta lia `t.recorrencia`, um campo que
  // fica com "Mensal" residual mesmo quando o usuário escolhe "Único" no
  // formulário (o campo só é limpo/reaproveitado quando o modo é
  // "Recorrente" — nos outros modos ele nunca é resetado). Por isso clicar
  // em "Único" nunca tirava a etiqueta de "Mensal". `modoLancamento` é a
  // fonte de verdade real do modo escolhido — usa ele primeiro; a lógica
  // antiga (parcela/recorrencia) fica só de fallback pra documentos
  // antigos sem `modoLancamento` gravado.
  // Achado real 18/09/2026 (2ª rodada): confiar cegamente em `modoLancamento`
  // quebrou lançamentos antigos que têm `parcela` preenchido de verdade
  // (ex: "7/10") mas `modoLancamento` desatualizado/errado (ex: "UNICO") —
  // aparecia "Única 7/10", uma combinação sem sentido. `parcela` truthy é o
  // sinal mais forte de que é Parcelado; só cai pro modoLancamento quando
  // não há parcela nenhuma.
  let recorrName;
  if (parcela) recorrName = "Parcelado";
  else if (t.modoLancamento === "UNICO") recorrName = "Única";
  else if (t.modoLancamento === "RECORRENTE") recorrName = t.recorrencia || "Mensal";
  else recorrName = t.recorrencia || "Única";
  const isInstallment = recorrName === "Parcelado";
  return (
    <span
      className={`inline-block px-1.5 py-0.5 rounded border font-bold text-[9px] ${
        isInstallment
          ? "bg-orange-50 text-orange-700 border-orange-200"
          : "bg-gray-100 text-gray-600 border-gray-200"
      }`}
    >
      {recorrName}
      {parcela && (
        <span className="ml-1 font-mono font-normal opacity-80">{parcela}</span>
      )}
    </span>
  );
}

// Badge de RESPONSÁVEL: rosa
function ResponsavelBadge({ vinculo }) {
  if (!vinculo) return <span className="text-[9px] text-gray-400">—</span>;
  const nome = vinculo.charAt(0).toUpperCase() + vinculo.slice(1);
  return (
    <span className="inline-block px-1.5 py-0.5 rounded border font-bold text-[9px] bg-pink-100 text-pink-700 border-pink-200">
      {nome}
    </span>
  );
}

// Badge de GRUPO (exibe o grupo pai, nao a subcategoria)
function GrupoBadge({ group, categories }) {
  const style = categories?.[group] || {
    color: "bg-gray-100 text-gray-600 border-gray-200",
  };
  return (
    <span
      className={`inline-block px-1.5 py-0.5 border font-medium text-[9px] whitespace-nowrap ${style.color}`}
    >
      {group}
    </span>
  );
}

// Verifica se esta atrasado (data de vencimento passou e nao foi pago)
function checkOverdue(t) {
  if (
    t.status === "PAID" ||
    t.status === "CANCELLED" ||
    t.status === "FORECAST"
  )
    return false;
  if (!t.date) return false;
  const due = new Date(t.date + "T23:59:59");
  const now = new Date();
  return due < now;
}

// Status: 🔴 Atrasado / 🟠 Aberto / ✅ Pago
function StatusCell({ t }) {
  const overdue = checkOverdue(t);
  if (t.status === "PAID") {
    return (
      <span className="inline-flex items-center gap-1">
        <span className="text-[10px]">✅</span>
        <span className="font-medium text-[10px] text-green-700">Pago</span>
      </span>
    );
  }
  if (t.status === "FORECAST") {
    return (
      <span className="inline-flex items-center gap-1">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-purple-400"></span>
        <span className="font-medium text-[10px] text-purple-600">
          Previsao
        </span>
      </span>
    );
  }
  if (overdue) {
    return (
      <span className="inline-flex items-center gap-1">
        <span className="text-[10px]">🔴</span>
        <span className="font-medium text-[10px] text-red-600">Atrasado</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      <span className="text-[10px]">🟠</span>
      <span className="font-medium text-[10px] text-orange-600">Aberto</span>
    </span>
  );
}

// Botoes de acao — em coluna propria, fora do valor
function ActionButtons({
  t,
  isPaid,
  isForecast,
  isPartOfPair,
  isFirstOfPair,
  onSettle,
  onEdit,
  onDuplicate,
  onDelete,
}) {
  return (
    <span className="inline-flex gap-0.5 opacity-100">
      {!isPaid &&
        !isForecast &&
        onSettle &&
        (!isPartOfPair || isFirstOfPair) && (
          <button
            onClick={() => onSettle(t)}
            className="text-green-500 hover:text-green-700 p-1"
            title="Liquidar"
            aria-label="Liquidar"
          >
            <i className="fas fa-check-double text-[8px]"></i>
          </button>
        )}
      <button
        onClick={() => onEdit(t)}
        className="text-gray-400 hover:text-blue-600 p-1"
        title="Editar"
        aria-label="Editar"
      >
        <i className="fas fa-pen text-[8px]"></i>
      </button>
      {onDuplicate && (
        <button
          onClick={() => onDuplicate(t)}
          className="text-gray-400 hover:text-[#C5A059] p-1"
          title="Duplicar (cria um novo lançamento parecido com este)"
          aria-label="Duplicar"
        >
          <i className="fas fa-copy text-[8px]"></i>
        </button>
      )}
      <button
        onClick={() => onDelete(t.id)}
        className="text-gray-400 hover:text-red-600 p-1"
        title="Excluir"
        aria-label="Excluir"
      >
        <i className="fas fa-trash text-[8px]"></i>
      </button>
    </span>
  );
}

/** Renderiza UMA linha <tr> */
function Row({
  t,
  categories,
  onEdit,
  onDuplicate,
  onDelete,
  onSettle,
  isPartOfPair,
  isFirstOfPair,
  isLastOfPair,
}) {
  const isPaid = t.status === "PAID";
  const isForecast = t.status === "FORECAST";
  const overdue =
    !isPaid &&
    !isForecast &&
    t.date &&
    new Date(t.date + "T23:59:59") < new Date();
  const effectiveStatus = overdue ? "OVERDUE" : t.status || "PENDING";

  const BORDER_MAP = {
    PAID: "border-l-[#059669]",
    OVERDUE: "border-l-[#dc2626]",
    PENDING: "border-l-[#d97706]",
    FORECAST: "border-l-[#7c3aed]",
    CANCELLED: "border-l-[#d1d5db]",
  };
  const BG_MAP = {
    PAID: "bg-[#ecfdf5]/40",
    OVERDUE: "bg-[#fef2f2]/40",
    PENDING: "bg-[#fffbeb]/30",
    FORECAST: "bg-[#f5f3ff]/30",
    CANCELLED: "bg-[#f9fafb]/40",
  };

  return (
    <tr
      className={`${BG_MAP[effectiveStatus] || ""} ${isPartOfPair && !isLastOfPair ? "" : "border-b border-gray-100"} ${isPartOfPair && isFirstOfPair ? "border-t border-gray-200" : ""} hover:bg-[#e8f0fe]/30 group transition-colors ${isPartOfPair ? `border-l-[3px] ${BORDER_MAP[effectiveStatus] || BORDER_MAP.PENDING}` : ""}`}
    >
      {/* DATA */}
      <td className="px-2 py-1 font-mono text-[10px] text-gray-700 whitespace-nowrap">
        {new Date(t.date + "T12:00:00").toLocaleDateString("pt-BR")}
      </td>
      {/* TIPO */}
      <td className="px-2 py-1 whitespace-nowrap">
        <TipoBadge t={t} />
      </td>
      {/* DESCRICAO */}
      <td className="px-2 py-1 text-gray-800 leading-tight text-[11px] font-medium">
        {stripArt(t.description)}
      </td>
      {/* CONTA */}
      <td className="px-2 py-1 text-[10px] text-gray-500 whitespace-nowrap">
        {t.account || <span className="text-gray-400">—</span>}
      </td>
      {/* RESPONSAVEL */}
      <td className="px-2 py-1">
        <ResponsavelBadge vinculo={t.vinculo} />
      </td>
      {/* CATEGORIA — exibe GRUPO (pai), nao subcategoria */}
      <td className="px-2 py-1">
        <GrupoBadge group={t.group} categories={categories} />
      </td>
      {/* STATUS */}
      <td className="px-2 py-1">
        <StatusCell t={t} />
      </td>
      {/* VALOR (R$) — so o numero, sem botoes */}
      <td className="px-2 py-1 text-right whitespace-nowrap w-[80px]">
        <span
          className={`font-mono font-bold tabular-nums text-[11px] ${t.group === "RECEITAS" ? "text-green-600" : "text-red-600"}`}
        >
          {formatNumber(t.value)}
        </span>
      </td>
      {/* ACOES — coluna separada */}
      <td className="px-2 py-1 whitespace-nowrap w-[60px]">
        <ActionButtons
          {...{
            t,
            isPaid,
            isForecast,
            isPartOfPair,
            isFirstOfPair,
            onSettle,
            onEdit,
            onDuplicate,
            onDelete,
          }}
        />
      </td>
    </tr>
  );
}

// ============================================================================
// Componente exportado (unico)
// ============================================================================
export function PairRow({ item, categories, onEdit, onDuplicate, onDelete, onSettle }) {
  const opts = { categories, onEdit, onDuplicate, onDelete, onSettle };

  if (item.type === "pair") {
    return (
      <>
        <Row
          t={item.income}
          {...opts}
          isPartOfPair
          isFirstOfPair
          isLastOfPair={false}
        />
        <Row
          t={item.expense}
          {...opts}
          isPartOfPair
          isFirstOfPair={false}
          isLastOfPair
        />
      </>
    );
  }

  return (
    <Row
      t={item.posting}
      {...opts}
      isPartOfPair={false}
      isFirstOfPair={false}
      isLastOfPair={false}
    />
  );
}
